import { useEffect } from 'react'
import { authStore } from './authStore'
import { readAuthConfig } from './ssoLogin'
import { SESSION_ENDED_CODE, endSsoSessionLocally } from './ssoSession'

/**
 * Asks the API, at the two moments it matters, whether the sso session behind
 * this browser's credential is still alive.
 *
 * Nothing is on a timer. The failure this exists for is a user who signed out of
 * another relying party and came back to this tab, so the two moments are the
 * page load and the tab regaining focus, and a floor keeps a burst of focus
 * events from turning into a burst of requests.
 */

/** Browser-side path. nginx and the Vite dev proxy both strip the /api prefix. */
const SESSION_PATH = '/api/session'

/** The contract's cache window, reused as the floor between two checks. */
const CHECK_FLOOR_MS = 30_000

/**
 * Caps the check. It runs on a page the user is already looking at, so a hung
 * request must not keep a connection and a pending promise alive indefinitely.
 */
const CHECK_TIMEOUT_MS = 5000

export type SsoSessionVerdict = 'alive' | 'ended' | 'unknown'

export interface CheckSsoSessionDeps {
  fetch: typeof globalThis.fetch
  getIdToken: () => Promise<string | undefined>
}

/**
 * One call to the session endpoint.
 *
 * Only a 401 that actually carries the session-ended code counts as ended.
 * Everything else -- a refused bearer, a 500, a dropped connection, a missing
 * token -- is `unknown`, because the only action available on `ended` is signing
 * the user out, and doing that on an ambiguous answer is worse than doing
 * nothing.
 */
export async function checkSsoSession(deps: CheckSsoSessionDeps): Promise<SsoSessionVerdict> {
  let response: Response
  try {
    // getIdToken() is inside this try too: it refreshes the token over the
    // network and can reject the same way the fetch below can (offline,
    // auth/network-request-failed), and that failure is exactly as ambiguous
    // as a failed fetch -- it must not escape as a rejection either.
    const token = await deps.getIdToken()
    if (token === undefined) return 'unknown'
    response = await deps.fetch(SESSION_PATH, {
      headers: { Authorization: `Bearer ${token}` },
      signal:
        typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(CHECK_TIMEOUT_MS) : undefined,
    })
  } catch (err) {
    console.warn('[ssoSession] the session check did not complete', err)
    return 'unknown'
  }

  if (response.status === 204) return 'alive'
  if (response.status !== 401) return 'unknown'

  const body = (await response.json().catch(() => undefined)) as { code?: unknown } | undefined
  return body?.code === SESSION_ENDED_CODE ? 'ended' : 'unknown'
}

export interface SsoSessionWatchDeps {
  /** False outside sso mode, where there is no sso session to watch. */
  enabled: () => boolean
  now: () => number
  check: () => Promise<SsoSessionVerdict>
  onSessionEnded: () => void
}

function productionWatchDeps(): SsoSessionWatchDeps {
  return {
    enabled: () => readAuthConfig().mode === 'sso',
    now: () => Date.now(),
    check: () =>
      checkSsoSession({
        fetch: (input, init) => globalThis.fetch(input, init),
        getIdToken: async () => {
          const snapshot = authStore.snapshot()
          return snapshot.status === 'signed-in' ? snapshot.user.getIdToken() : undefined
        },
      }),
    onSessionEnded: () => { void endSsoSessionLocally() },
  }
}

/**
 * Mount once, at the app root, in every mode. `signedInUid` is what restarts it:
 * the app root renders before anyone is signed in, and keying the effect on the
 * session identity means the watch begins when a session appears and begins
 * again when a different user takes its place.
 */
export function useSsoSessionWatch(
  signedInUid: string | undefined,
  deps: SsoSessionWatchDeps = productionWatchDeps(),
): void {
  useEffect(() => {
    if (signedInUid === undefined || !deps.enabled()) return

    let disposed = false
    let lastCheckedAt = Number.NEGATIVE_INFINITY

    const run = () => {
      const at = deps.now()
      if (at - lastCheckedAt < CHECK_FLOOR_MS) return
      lastCheckedAt = at
      void deps
        .check()
        .then((verdict) => {
          if (!disposed && verdict === 'ended') deps.onSessionEnded()
        })
        // Defensive: checkSsoSession already turns every failure into 'unknown',
        // but a test double or a future change to `deps.check` could still
        // reject, and a check nobody awaits must never surface as an unhandled
        // rejection.
        .catch(() => {})
    }

    run()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') run()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    // `deps` is a test seam rebuilt on every render, so it deliberately stays out
    // of the dependency list: the session identity is the only thing that should
    // restart the watch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedInUid])
}
